import React from 'react';
import { styled } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VideocamIcon from '@mui/icons-material/Videocam';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from "@mui/icons-material/Search";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SelectedCharacteristics from "../characteristics/SelectedCharcteristics";
import SignInManager from "../../components/SignInManager";
import { UserSignedInAndSubscriptionActive } from "../../services/user/userStatusService";
import { userIsAdministrator } from "../../services/user/cognitoService";

const drawerWidth = "auto";

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

/**
 * SearchAppBar - Top navigation bar with all action buttons
 * @param {boolean} drawerOpen - Whether the filter drawer is open
 * @param {Function} onHistoryOpen - Handler for opening history drawer
 * @param {Function} onCameraOpen - Handler for opening camera modal
 * @param {Function} onVideoRecorderOpen - Handler for opening video recorder modal
 * @param {Function} onStealthOpen - Handler for opening stealth camera
 * @param {Function} onDrawerOpen - Handler for opening filter drawer
 * @param {Function} onSettingsOpen - Handler for opening settings
 * @param {Function} onAdminOpen - Handler for opening admin modal
 * @param {Object} cognitoUser - Current authenticated user
 * @param {boolean} stealthMode - Whether stealth mode is enabled
 * @param {boolean} videoEnabled - Whether stealth video capture is enabled
 * @param {Array} myarr - Selected characteristics array
 * @param {boolean} isAdminMode - Whether rendering in admin mode (hides camera/history)
 */
export default function SearchAppBar({
  drawerOpen,
  onHistoryOpen,
  onCameraOpen,
  onVideoRecorderOpen,
  onStealthOpen,
  onDrawerOpen,
  onSettingsOpen,
  onAdminOpen,
  cognitoUser,
  stealthMode,
  videoEnabled,
  myarr,
  isAdminMode = false
}) {
  const isUserActive = UserSignedInAndSubscriptionActive(cognitoUser);
  const isAdmin = userIsAdministrator(cognitoUser);


  return (
    <AppBar position="fixed" open={drawerOpen} sx={{ bgcolor: '#2d3e50' }}>
      <Toolbar>
        {!isAdminMode && (
          <>
            <IconButton
              color="inherit"
              aria-label="history"
              onClick={onHistoryOpen}
              edge="start"
              sx={{ mr: 2, ...(drawerOpen && { display: "none" }) }}
            >
              <WorkHistoryIcon />
            </IconButton>

            <IconButton
              aria-label="camera"
              color="inherit"
              onClick={onCameraOpen}
              disabled={!isUserActive}
              title="Take Photo"
            >
              <CameraAltIcon />
            </IconButton>

            <IconButton
              aria-label="video recorder"
              color="inherit"
              onClick={onVideoRecorderOpen}
              disabled={!isUserActive || !videoEnabled}
              title="Stealth Video Capture"
            >
              <VideocamIcon />
            </IconButton>

            <IconButton
              aria-label="stealth camera"
              color="inherit"
              onClick={onStealthOpen}
              disabled={!isUserActive || !stealthMode}
              title="Stealth Mode"
            >
              <VisibilityOffIcon />
            </IconButton>
          </>
        )}

        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={onDrawerOpen}
          sx={{ mr: 2, ...(drawerOpen && { display: "none" }) }}
        >
          <SearchIcon />
        </IconButton>

        <SelectedCharacteristics myarr={myarr} />

        <Box sx={{ flexGrow: 1 }} />

        <SignInManager />

        {isAdmin && !isAdminMode && (
          <IconButton
            aria-label="admin"
            color="inherit"
            onClick={onAdminOpen}
            title="Administration"
          >
            <AdminPanelSettingsIcon />
          </IconButton>
        )}

        <IconButton
          aria-label="settings"
          color="inherit"
          onClick={onSettingsOpen}
          disabled={!isUserActive}
        >
          <SettingsIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
}
